import type { Question, QuizMode, Unit } from '@/types';

export function shuffle<T>(input: readonly T[]): T[] {
  const a = [...input];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const EXAM_COUNT = 40;
export const EXAM_DURATION_MS = 90 * 60 * 1000;
export const FIT30_DURATION_MS = 45 * 60 * 1000;

/** How long a mode is given, or null when it is untimed. */
export function durationForMode(mode: QuizMode): number | null {
  if (mode === 'fit30') return FIT30_DURATION_MS;
  if (mode === 'final40') return EXAM_DURATION_MS;
  return null;
}

export function resolveCount(mode: QuizMode, total: number): number {
  if (typeof mode === 'object') return Math.min(mode.count, total);
  switch (mode) {
    case 'full': return total;
    case 'fit30': return Math.min(30, total);
    case 'quick10': return Math.min(10, total);
    case 'readingcheck': return Math.min(10, total);
    case 'final40': return Math.min(EXAM_COUNT, total);
  }
}

/**
 * Draw `count` questions spread evenly across the units feeding an exam pool,
 * so a 40-question final over three units is 14/13/13 rather than whatever the
 * relative pool sizes happen to produce. Any remainder goes to the largest
 * pools first, and a unit that cannot fill its share passes it on.
 */
function drawStratified(questions: readonly Question[], count: number): Question[] {
  const groups = new Map<string, Question[]>();
  for (const q of questions) {
    const key = q.srcUnitId ?? '';
    const bucket = groups.get(key);
    if (bucket) bucket.push(q);
    else groups.set(key, [q]);
  }
  if (groups.size < 2) return shuffle(questions).slice(0, count);

  const order = [...groups.keys()].sort(
    (a, b) => groups.get(b)!.length - groups.get(a)!.length,
  );
  const base = Math.floor(count / order.length);
  const extra = count % order.length;

  const take = new Map<string, number>();
  let shortfall = count;
  order.forEach((key, i) => {
    const want = base + (i < extra ? 1 : 0);
    const got = Math.min(want, groups.get(key)!.length);
    take.set(key, got);
    shortfall -= got;
  });
  for (const key of order) {
    if (shortfall <= 0) break;
    const spare = groups.get(key)!.length - take.get(key)!;
    const add = Math.min(spare, shortfall);
    take.set(key, take.get(key)! + add);
    shortfall -= add;
  }

  return shuffle(order.flatMap((key) => shuffle(groups.get(key)!).slice(0, take.get(key)!)));
}

function shuffleOptions(qs: Question[]): Question[] {
  return qs.map((q) => {
    if (q.type === 'select-all') return { ...q, opts: [...q.opts], ans: [...q.ans] };
    const indices = q.opts.map((_, i) => i);
    const shuffled = shuffle(indices);
    return {
      ...q,
      opts: shuffled.map((i) => q.opts[i]),
      ans: q.ans.map((origIdx) => shuffled.indexOf(origIdx)),
    };
  });
}

export function buildQuestions(unit: Unit, mode: QuizMode): Question[] {
  if (mode === 'readingcheck') {
    const pool = unit.readingCheck?.questions ?? [];
    const count = Math.min(unit.readingCheck?.questionsPerSession ?? 10, pool.length);
    return shuffleOptions(shuffle(pool).slice(0, count));
  }
  const count = resolveCount(mode, unit.questions.length);
  if (mode === 'final40') return shuffleOptions(drawStratified(unit.questions, count));
  return shuffleOptions(shuffle(unit.questions).slice(0, count));
}

export function isCorrect(q: Question, answer: number[] | null): boolean {
  if (!answer) return false;
  if (q.type === 'select-all') {
    const a = [...answer].sort();
    const b = [...q.ans].sort();
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return answer[0] === q.ans[0];
}

export function gradeFor(pct: number): { label: string; color: string } {
  if (pct >= 90) return { label: 'Outstanding!', color: 'var(--correct)' };
  if (pct >= 80) return { label: 'Great job!', color: 'var(--correct)' };
  if (pct >= 70) return { label: 'Good effort!', color: 'var(--gold)' };
  return { label: 'Keep studying!', color: 'var(--wrong)' };
}
